import moment from "moment";
import { collectExerciseNamesFromExercise } from "./exerciseNameMatch";

function formatSetLine(set) {
  if (!set) return "";
  const parts = [];
  const reps = set.reps;
  const weight = set.weight;
  const rpe = set.rpe;
  const time = set.time;
  const timeUnit = set.timeUnit;

  if (reps != null && reps !== "") {
    parts.push(String(reps));
  }
  if (weight != null && weight !== "") {
    parts.push(`@ ${weight}`);
  }
  if (rpe != null && rpe !== "") {
    parts.push(`rpe ${rpe}`);
  }
  if (time != null && time !== "") {
    const unit = timeUnit === "min" ? "min" : "sec";
    parts.push(`${time} ${unit}`);
  }
  return parts.join(" ").trim();
}

function formatSetsSummary(exercise) {
  if (!exercise) return "";

  const t = String(exercise.exerciseType || exercise.type || "").toLowerCase();
  const isCircuit =
    t === "circuit" ||
    (Array.isArray(exercise.circuit) && exercise.circuit.length > 0) ||
    (Array.isArray(exercise.subExercises) && exercise.subExercises.length > 0);

  if (isCircuit) {
    const rounds =
      exercise.rounds ||
      (Array.isArray(exercise.circuit) ? exercise.circuit.length : 1);
    const names = [];
    const circuit = exercise.circuit;
    if (Array.isArray(circuit) && circuit[0]) {
      for (const sub of circuit[0]) {
        if (sub?.name) names.push(String(sub.name).trim());
      }
    } else if (Array.isArray(exercise.subExercises)) {
      for (const sub of exercise.subExercises) {
        if (sub?.name) names.push(String(sub.name).trim());
      }
    }
    const roundLabel = rounds === 1 ? "1 round" : `${rounds} rounds`;
    return names.length
      ? `Circuit (${roundLabel}): ${names.join(", ")}`
      : `Circuit (${roundLabel})`;
  }

  const sets = exercise.sets || [];
  const setLines = sets
    .map(formatSetLine)
    .filter(Boolean);
  if (setLines.length === 0) return "";
  if (setLines.length === 1) return setLines[0];

  const nxm = sets.length > 1 && sets.every((s) => {
    const r = s?.reps;
    return r != null && r !== "" && String(r) === String(sets[0]?.reps);
  });
  if (nxm && sets[0]?.reps) {
    const rpe = sets[0]?.rpe;
    return rpe ? `${sets.length}x${sets[0].reps} rpe ${rpe}` : `${sets.length}x${sets[0].reps}`;
  }
  return setLines.join("; ");
}

function formatExerciseBlock(exercise, indent = "    ") {
  const name = exercise?.name ? String(exercise.name).trim() : "Exercise";
  const setsSummary = formatSetsSummary(exercise);
  const desc = exercise?.description ? String(exercise.description).trim() : "";
  const lines = [`${indent}${name}`];
  if (setsSummary) lines.push(`${indent}  ${setsSummary}`);
  if (desc) lines.push(`${indent}  ${desc.replace(/\n/g, "\n" + indent + "  ")}`);
  return lines.join("\n");
}

/**
 * Plain-text summary of workouts for Bedrock context.
 * @param {object[]} workouts — sorted by date ascending
 */
export function workoutsToContextSummary(workouts) {
  if (!Array.isArray(workouts) || workouts.length === 0) {
    return "No workouts in the last month.";
  }

  const sorted = [...workouts].sort((a, b) => (a.date || 0) - (b.date || 0));
  const blocks = [];

  for (const w of sorted) {
    const day = w.date ? moment.unix(w.date).format("dddd, MMM D, YYYY") : "Unknown date";
    const title = w.name ? String(w.name).trim() : "Untitled workout";
    const desc = w.description ? String(w.description).trim() : "";
    const lines = [`${day} — ${title}`];
    if (desc) lines.push(`  ${desc.replace(/\n/g, "\n  ")}`);

    const exercises = w.exercises || [];
    if (exercises.length === 0) {
      lines.push("  (no exercises logged)");
    } else {
      for (const ex of exercises) {
        lines.push(formatExerciseBlock(ex));
      }
    }
    blocks.push(lines.join("\n"));
  }

  return blocks.join("\n\n");
}

/** Distinct exercise names across workouts (for avoid list). */
export function exerciseNamesFromWorkouts(workouts) {
  const names = new Set();
  for (const w of workouts || []) {
    for (const ex of w.exercises || []) {
      for (const n of collectExerciseNamesFromExercise(ex)) {
        names.add(n);
      }
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}
